<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Taxonomies', path: {name: 'admin-taxonomies'} }]" name="Subcategories" no-title/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Subcategories">
          <template v-slot:header-right>
            <b-form-select
                class="w-auto mr-2"
                v-model="filters.category_type"
                :options="categoryTypeOptions"
            />
            <b-form-select
                class="w-auto mr-2"
                v-model="filters.category"
                :options="categoryOptions"
            />
            <no-permission-wrapper :show="!canCreateTaxonomies">
              <b-btn variant="success" @click="openNewModal" :disabled="!canCreateTaxonomies">Add new Subcategory</b-btn>
            </no-permission-wrapper>
          </template>
          <b-table
              :fields="table_fields"
              :items="subcategories"
              table-class="text-nowrap"
              responsive
          >
            <template #cell(name)="data">
              <h4 class="mb-0">{{ data.item.name }}</h4>
            </template>
            <template #cell(category_types)="data">
              <div v-for="(ct, key) in data.item.category_types" :key="key">
                {{ ct.name }}
              </div>
            </template>
            <template #cell(categories)="data">
              <div v-for="(c, key) in data.item.categories" :key="key">
                {{ c.name }}
              </div>
            </template>
            <template #cell(actions)="data">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn variant="primary" @click.prevent="openEditModal(data.item)" :disabled="!canUpdateTaxonomies">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper :show="!canDeleteTaxonomies">
                <b-btn variant="danger" @click.prevent="confirmDelete(data.item)" :disabled="!canDeleteTaxonomies">Delete</b-btn>
              </no-permission-wrapper>
            </template>
          </b-table>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-subcategory-edit-modal
        v-if="edit_subcategory"
        :subcategory="edit_subcategory"
        @cancel="edit_subcategory = null"
        @success="onSubcategoryEdited"
        visible
    />
    <admin-subcategory-add-modal
      ref="newModal"
      @success="onSubcategoryAdded"
    />
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader.vue";
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import AdminPanel from "../components/AdminPanel.vue";
import AdminSubcategoryAddModal from "../components/AdminSubcategoryAddModal.vue";
import {sortingStrFunc} from "../helpers";
import AdminSubcategoryEditModal from "../components/AdminSubcategoryEditModal.vue";
import AdminCategoryEditModal from "../components/AdminCategoryEditModal.vue";
import {API_ROUTES} from "../api";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminSubcategories",
  components: {
    NoPermissionWrapper,
    AdminCategoryEditModal,
    AdminSubcategoryEditModal, AdminSubcategoryAddModal, AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    return {
      loading: false,
      table_fields: [
        {
          key: "name",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "category_types",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "categories",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "actions",
          thClass: "border-top-0",
          tdClass: "align-middle"
        }
      ],
      edit_subcategory: null,
      filters: {
        category_type: null,
        category: null,
      }
    }
  },
  computed: {
    canCreateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_create');
    },
    canUpdateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_update');
    },
    canDeleteTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_delete');
    },
    search() {
      return this.$store.state.current.search;
    },
    subcategories() {
      let subcategories = [...this.$store.state.data.subcategories];
      if (this.search) {
        let search = this.search.toLowerCase();
        subcategories = subcategories.filter(c => c.name.toLowerCase().indexOf(search) >= 0);
      }

      for (let i = 0; i < subcategories.length; i++) {
        let subcategory = subcategories[i],
            categoryTypes = [],
            categories = [];
        if (subcategory.belongs_to_category_type) {
          categoryTypes = this.categoryTypes.filter(ct => subcategory.category_types.indexOf(ct.id) >= 0);
        } else if (subcategory.belongs_to_category) {
          categories = this.categories.filter(c => subcategory.categories.indexOf(c.id) >= 0);
        }

        subcategories[i] = {
          ...subcategory,
          category_types: categoryTypes,
          categories: categories
        }
      }

      if (this.filters.category_type) {
        subcategories = subcategories.filter(sc => {
          if (sc.category_types.find(ct => ct.id === this.filters.category_type)) {
            return true;
          }

          for (let i = 0; i < sc.categories.length; i++) {
            if (sc.categories[i].category_type_id === this.filters.category_type) {
              return true;
            }
          }

          return false;
        });
      }

      if (this.filters.category) {
        subcategories = subcategories.filter(sc => sc.categories.find(c => c.id === this.filters.category));
      }

      return subcategories;
    },
    categoryTypes() {
      return [...this.$store.state.data.category_types].sort((first, second) => sortingStrFunc(first.name, second.name));
    },
    categoryTypeOptions() {
      return [
        {
          value: null,
          text: "All Category Types"
        },
        ...this.categoryTypes.map(ct => ({
          value: ct.id,
          text: ct.name
        }))
      ];
    },
    categories() {
      let categories = [];

      this.categoryTypes.forEach(ct => {
        let ctCats = [...this.$store.state.data.categories].filter(c => {
          return c.category_type_id === ct.id &&
              (!this.filters.category_type || this.filters.category_type === c.category_type_id)
        }).sort((first, second) => sortingStrFunc(first.name, second.name))
            .map(c => ({
              ...c,
              name: !this.filters.category_type ? `${ct.name} - ${c.name}` : c.name
            }));

        if (ctCats.length > 0) {
          categories = [
            ...categories,
            ...ctCats
          ]
        }
      });

      return categories;
    },
    categoryOptions() {
      return [
        {
          value: null,
          text: "All Categories"
        },
        ...this.categories.map(c => ({
          value: c.id,
          text: c.name
        }))
      ];
    },
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    openNewModal() {
      if (!this.checkPermission('canCreateTaxonomies')) {
        return;
      }
      this.$refs.newModal.open();
    },
    onSubcategoryAdded(subcategory) {
      this.$bvToast.toast('Subcategory has been successfully created', {
        title: 'Success!',
        variant: 'success'
      });
      let subcategories = [...this.$store.state.data.subcategories, subcategory];
      this.$store.commit('data/setSubcategories', subcategories);
    },
    openEditModal(subcategory) {
      if (!this.checkPermission('canUpdateTaxonomies')) {
        return;
      }
      this.edit_subcategory = subcategory;
    },
    onSubcategoryEdited(subcategory) {
      let subcategories = [...this.$store.state.data.subcategories],
          index = subcategories.findIndex(sc => sc.id === subcategory.id);
      if (index >= 0) {
        subcategories[index] = subcategory;
      } else {
        subcategories.push(subcategory)
      }
      this.$store.commit('data/setSubcategories', subcategories);
    },
    confirmDelete(subcategory) {
      if (!this.checkPermission('canDeleteTaxonomies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm(`You are going to delete ${subcategory.name} subcategory`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_SUBCATEGORIES}/${subcategory.id}/delete`).then(() => {
            let subcategories = [...this.$store.state.data.subcategories],
                index = subcategories.findIndex(sc => sc.id === subcategory.id);

            if (index >= 0) {
              subcategories.splice(index, 1);
              this.$store.commit('data/setSubcategories', subcategories);
            }

            this.$bvToast.toast('Subcategory has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
  },
  watch: {
    "filters.category_type": function (ctId) {
      if (ctId && this.filters.category) {
        let category = this.categories.find(c => c.id === this.filters.category);
        if (!category || category.category_type_id !== ctId) {
          this.filters.category = null;
        }
      }
    }
  }
}
</script>
